import React from "react";
import { Element } from "react-scroll";
import { useSelector } from "react-redux";

import usePublicCustomers from "./usePublicCustomers";
import "./farmPartners.css";

const textData = {
  subtitle: [
    "Vi är stolta över att samarbeta med några av de bästa återförsäljarna i närområdet för att leverera våra färska och näringsrika grödor till ditt bord. Genom dessa samarbeten kan vi nå fler kunder och sprida fördelarna med våra hållbart odlade microgreens.",
    "We're proud to work with some of the best retailers in the area to bring our fresh and nutritious microgreens to your table. These partnerships allow us to reach more customers and share the benefits of our sustainably grown microgreens with the local community.",
  ],
  subtitle2: [
    "På den här sidan presenterar vi våra betrodda återförsäljare och ger dig all information du behöver för att hitta våra microgreens i butik eller hos en återförsäljare nära dig. Oavsett om du är hemmakock, restaurangägare eller matentusiast är vi glada att ha dig med oss i vårt uppdrag att främja hälsosam och god mat.",
    "On this page, we'll introduce our trusted retailers and provide all the information you need to find our microgreens in a store or through a reseller near you. Whether you're a home cook, restaurant owner, or food enthusiast, we're excited to have you with us in our mission to promote healthy and delicious food.",
  ],
  store1: ["BUTIK", "STORE"],
  store2: ["GROSSIST", "RESELLER"],
  store3: ["ÖVRIGT", "OTHER"],
  header: ["ÅTERFÖRSÄLJARE", "RETAILERS"],
  loading: ["Hämtar återförsäljare…", "Loading retailers…"],
  error: ["Återförsäljarna kunde inte hämtas.", "Retailers could not be loaded."],
  retry: ["Försök igen", "Try again"],
  empty: ["Inga återförsäljare att visa just nu.", "No retailers available right now."],
};

const websiteUrl = (value) => {
  const address = typeof value === "string" ? value.trim() : "";
  if (!address) return null;
  try {
    const url = new URL(/^https?:\/\//i.test(address) ? address : `https://${address}`);
    return ["http:", "https:"].includes(url.protocol) ? url.href : null;
  } catch {
    return null;
  }
};

function PartnerCard({ customer, userLanguage }) {
  const url = websiteUrl(customer.url);
  const cardContent = (
    <>
      <span className="FarmPartnersCardVisual">
        {customer.image ? (
          <img src={customer.image} alt="" loading="lazy" className="FarmPartnersLogo" />
        ) : (
          <span className="FarmPartnersLogoFallback" aria-hidden="true">
            {customer.name.slice(0, 1).toLocaleUpperCase("sv")}
          </span>
        )}
      </span>
      <span className="FarmPartnersCardBody">
        <strong className="FarmPartnersCardName">{customer.name}</strong>
        {customer.address && <span className="FarmPartnersCardAddress">{customer.address}</span>}
        {url && <span className="FarmPartnersCardVisit" aria-hidden="true">
          {userLanguage === 0 ? "Besök webbplats" : "Visit website"}<span>↗</span>
        </span>}
      </span>
    </>
  );

  return (
    <article className="FarmPartnersCard">
      {url ? (
        <a href={url} target="_blank" rel="noopener noreferrer" className="FarmPartnersCardLink" aria-label={`${customer.name} – ${userLanguage === 0 ? "besök webbplats" : "visit website"}`}>
          {cardContent}
        </a>
      ) : (
        <div className="FarmPartnersCardLink">{cardContent}</div>
      )}
    </article>
  );
}

function FarmPartners() {
  const userLanguage = useSelector((state) => state.app.language);
  const { customers, loading, error, retry } = usePublicCustomers();
  const groups = [
    { category: "store", title: textData.store1[userLanguage] },
    { category: "retail", title: textData.store2[userLanguage] },
    { category: "other", title: textData.store3[userLanguage] },
  ].map((group) => ({
    ...group,
    items: customers.filter((customer) => customer.category === group.category),
  }));
  const visibleCount = groups.reduce((count, group) => count + group.items.length, 0);

  return (
    <Element name="farm/partners" className="FarmPartners" id="farm-partners">
      <div className="FarmLandingContainer">
        <header className="FarmLandingSectionHeading FarmPartnersHeading">
          <h2>{textData.header[userLanguage]}</h2>
          <div className="FarmPartnersIntro"><p>{textData.subtitle[userLanguage]}</p><p>{textData.subtitle2[userLanguage]}</p></div>
        </header>
        {loading && <p className="FarmPartnersMessage" role="status">{textData.loading[userLanguage]}</p>}
        {error && <div className="FarmPartnersMessage" role="alert">
          <p>{textData.error[userLanguage]}</p>
          <button type="button" onClick={retry}>{textData.retry[userLanguage]}</button>
        </div>}
        {!loading && !error && visibleCount === 0 && (
          <p className="FarmPartnersMessage" role="status">{textData.empty[userLanguage]}</p>
        )}
        {!loading && !error && visibleCount > 0 && (
          <div className="FarmPartnersListInner">
            {groups.filter((group) => group.items.length > 0).map(({ category, title, items }) => (
              <section className="FarmPartnersCategory" key={category} aria-labelledby={`farm-partners-${category}`}>
                <div className="FarmPartnersCategoryHeading">
                  <h2 id={`farm-partners-${category}`}>{title}</h2>
                  <span className="FarmPartnersCategoryCount" aria-label={`${items.length} ${userLanguage === 0 ? "kunder" : "customers"}`}>{items.length}</span>
                </div>
                <div className="FarmPartnersCategoryGrid">
                  {items.map((customer) => (
                    <PartnerCard key={customer.firebaseId} customer={customer} userLanguage={userLanguage} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </Element>
  );
}

export default FarmPartners;
